import { UnfoldMore } from '@mui/icons-material'
import { Box, Chip, Container, Grid, Popover } from '@mui/material'
import type { GridRenderCellParams } from '@mui/x-data-grid'
import type React from 'react'
import { useMemo, useState } from 'react'
import { useAnchorToCenter } from '@/layout/hooks/useAnchorToCenter'

type Props = GridRenderCellParams

export const ExpandableCell = (props: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | Element>(null)
  const isOpen = Boolean(anchorEl)

  const popoverOrigins = useAnchorToCenter('vertical', anchorEl)

  const handleOpen = (event: React.MouseEvent) => {
    event.stopPropagation()
    const anchor = isOpen ? null : event.currentTarget
    setAnchorEl(anchor)
  }

  const handleMouseLeave = () => {
    handleClose()
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  if (!Array.isArray(props.value)) return null

  if (props.value.length === 0) return ''
  if (props.value.length === 1) return props.value[0]

  const sortedValues = useMemo(
    () => props.value.flat().sort((a: any, b: any) => (a < b ? -1 : 1)),
    [props.value],
  )

  const sortedValueString = useMemo(
    () => sortedValues.join(', '),
    [sortedValues],
  )

  return (
    <>
      <Box
        display={'flex'}
        alignItems="center"
        height="100%"
        width="100%"
        onClick={handleOpen}
        textOverflow="ellipsis"
        overflow="hidden"
        whiteSpace="nowrap"
        role="button"
        title="expandable contents"
        sx={{ cursor: 'pointer', position: 'relative' }}
      >
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexGrow: 1,
          }}
        >
          {sortedValueString}
        </span>
        <UnfoldMore opacity={0.6} fontSize="small" />
      </Box>
      <Popover
        {...popoverOrigins}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          paper: {
            onMouseLeave: handleMouseLeave,
          },
        }}
        disableScrollLock
      >
        <Container maxWidth="xl">
          <Box padding={2}>
            <Grid container spacing={1} columns={4}>
              {sortedValues.map((value: string, idx: number) => (
                <ExpandedContents key={`${value}-${idx}`} value={value} />
              ))}
            </Grid>
          </Box>
        </Container>
      </Popover>
    </>
  )
}

type ExpandedContentsProps = {
  value?: string
}

const ExpandedContents = (props: ExpandedContentsProps) => {
  if (!props.value) return null
  return <Chip key={props.value} label={props.value} />
}
